import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import moment from "moment";
import { timesService } from "services/timesService";
import { shouldIncludeSupervised, formatNumber } from "utils/utils";
import { createApprovalLock } from "utils/approvalInFlight";
import { TIME_ALERTS, isTimeRegistrationBreakAlert } from "components/Times/utils";
import { useTranslation } from 'react-i18next';

const formatSecondsToTime = (seconds, format = "hms") => {
	if (!seconds) return "0h 00m 00s";

	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const secs = seconds % 60;
	const formattedHours = formatNumber(hours);

	if (format === "hm") {
		return `${formattedHours}h ${minutes.toString().padStart(2, "0")}m`;
	}

	return `${formattedHours}h ${minutes.toString().padStart(2, "0")}m ${secs
		.toString()
		.padStart(2, "0")}s`;
};

const getEmployeeIdsFromItems = (items) => {
	const ids = new Set();
	items.forEach((item) => {
		const empId = item.employee?.id || item.employee_id;
		if (empId) ids.add(String(empId));
	});
	return [...ids];
};

/**
 * Hook para gestionar los datos de asistencia
 * @param {Object} filterParams - Parámetros de filtro
 * @param {Object} options
 * @param {Function} options.onNewEmployeeIds - Callback que recibe los IDs de empleados nuevos
 *   descubiertos en cada página (los mismos que se usan para alertas).
 */
export default function useAttendanceData(filterParams, { onNewEmployeeIds } = {}) {
	const { t } = useTranslation();
	const onNewEmployeeIdsRef = useRef(onNewEmployeeIds);
	onNewEmployeeIdsRef.current = onNewEmployeeIds;
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState(null);

	const [hasMore, setHasMore] = useState(true);
	const approvalLockRef = useRef(createApprovalLock());
	const [approvingIds, setApprovingIds] = useState(() => new Set());
	const [page, setPage] = useState(0);
	const [itemsPerPage] = useState(40);

	const abortControllerRef = useRef(null);

	// Cache de alertas — solo refs, sin estado
	const alertsCacheRef = useRef([]);
	const fetchedAlertEmployeeIdsRef = useRef(new Set());

	const [sortConfig, setSortConfig] = useState({
		field: "employee",
		direction: "asc",
	});

	const [aggregates, setAggregates] = useState({
		overall_seconds: 0,
		working_seconds: 0,
	});

	const getSortParam = useCallback(() => {
		const fieldMapping = {
			day: "day",
			employee: "employee",
			start: "start",
			end: "end",
			worked_time: "worked_time",
		};

		const apiField = fieldMapping[sortConfig.field] || sortConfig.field;
		const direction = sortConfig.direction === "asc" ? "+" : "-";

		if (sortConfig.field === "day") {
			return `${direction}${apiField}`;
		} else {
			return `${direction}${apiField},+day`;
		}
	}, [sortConfig]);

	const handleSort = useCallback((field) => {
		setSortConfig((prev) => ({
			field,
			direction:
				prev.field === field && prev.direction === "asc" ? "desc" : "asc",
		}));
		setPage(0);
		setItems([]);
	}, []);

	/** Orden explícito (p. ej. cabecera DataTable controlada). */
	const applySort = useCallback((field, direction) => {
		setSortConfig({ field, direction });
		setPage(0);
		setItems([]);
	}, []);

	const enrichTimeListWithAlerts = useCallback((timeList, alertsData = []) => {
		const alertsByRegistrationId = {};
		const alertsByEmployeeAndDay = {};

		alertsData.forEach((alert, index) => {
			if (!alert || typeof alert !== "object") return;

			const alertId = alert.id || `generated_id_${index}`;
			const processedAlert = {
				...alert,
				id: alertId,
				description:
					TIME_ALERTS[alert.type] || alert.type || t('attendance.noAlertDesc'),
				level: isTimeRegistrationBreakAlert(alert.type) ? "warning" : "danger",
			};

			if (alert.time_registration_id) {
				if (!alertsByRegistrationId[alert.time_registration_id]) {
					alertsByRegistrationId[alert.time_registration_id] = [];
				}
				alertsByRegistrationId[alert.time_registration_id].push(processedAlert);
			}

			if (alert.employee_id && alert.day) {
				const key = `${alert.employee_id}_${alert.day}`;
				if (!alertsByEmployeeAndDay[key]) {
					alertsByEmployeeAndDay[key] = [];
				}
				alertsByEmployeeAndDay[key].push(processedAlert);
			}
		});

		const MAX_ALERTS_PER_RECORD = 20;

		return timeList.map((item) => {
			const specificAlerts = item.id
				? alertsByRegistrationId[item.id] || []
				: [];

			const dayStr = item.day
				? moment(item.day).format("YYYY-MM-DD")
				: item.start
				? moment(item.start).format("YYYY-MM-DD")
				: "";

			const generalAlerts =
				item.employee_id && dayStr
					? alertsByEmployeeAndDay[`${item.employee_id}_${dayStr}`] || []
					: [];

			const alertIds = new Set(specificAlerts.map((a) => a.id));
			const combinedAlerts = [...specificAlerts];
			generalAlerts.forEach((alert) => {
				if (!alertIds.has(alert.id)) {
					combinedAlerts.push(alert);
				}
			});

			const limitedAlerts =
				combinedAlerts.length > MAX_ALERTS_PER_RECORD
					? combinedAlerts.slice(0, MAX_ALERTS_PER_RECORD)
					: combinedAlerts;

			// Preservar referencia si las alertas no cambiaron (evita re-render de la fila)
			const existingCount = item.alerts?.length || 0;
			if (limitedAlerts.length === 0 && existingCount === 0) {
				return item;
			}
			if (
				limitedAlerts.length === existingCount &&
				existingCount > 0 &&
				item.alerts.every((a, i) => a.id === limitedAlerts[i]?.id)
			) {
				return item;
			}

			return {
				...item,
				alert: limitedAlerts.length > 0 ? limitedAlerts[0] : null,
				alerts: limitedAlerts,
			};
		});
	}, []);

	// Obtener alertas — acepta employee_ids para filtrar
	const getTimeAlerts = useCallback(
		async (signal, employeeIds = null) => {
			try {
				const apiParams = {
					employee_ids: employeeIds
						? employeeIds.join(",")
						: filterParams?.employeeIds || "",
					start_from: filterParams?.startDate || "",
					start_to: filterParams?.endDate || "",
					group_id: filterParams?.groupId || "",
					page: 0,
					sort: getSortParam(),
					not_ended: filterParams?.notEnded === "1",
					include_supervised: shouldIncludeSupervised(),
					pending_only: filterParams?.approved === "1",
				};

				const alertsResponse = await timesService.alerts(apiParams, signal);

				let alertsData = [];
				if (Array.isArray(alertsResponse)) {
					alertsData = alertsResponse;
				} else if (
					alertsResponse &&
					alertsResponse.data &&
					Array.isArray(alertsResponse.data)
				) {
					alertsData = alertsResponse.data;
				} else if (alertsResponse && typeof alertsResponse === "object") {
					const possibleArrays = Object.values(alertsResponse).filter(
						Array.isArray
					);
					if (possibleArrays.length > 0) {
						alertsData = possibleArrays.reduce(
							(a, b) => (a.length > b.length ? a : b),
							[]
						);
					} else {
						return [];
					}
				} else {
					return [];
				}
				return alertsData;
			} catch (err) {
				if (err.name === "AbortError") throw err;
				console.error("Error al obtener alertas:", err);
				return [];
			}
		},
		[filterParams, getSortParam]
	);

	const fetchMoreData = useCallback(() => {
		// No encadenar fetches: si ya hay una carga en curso (inicial o de
		// "cargar más"), ignoramos el trigger. Esto evita que InfiniteScroll
		// dispare múltiples incrementos de página al 90% del scroll.
		if (!loading && !loadingMore && hasMore) {
			setPage((prev) => prev + 1);
		}
	}, [loading, loadingMore, hasMore]);

	const handleApprove = useCallback((id, approved) => {
		const key = String(id);
		if (!approvalLockRef.current.tryBegin(key)) {
			return;
		}
		setApprovingIds((prev) => {
			const next = new Set(prev);
			next.add(key);
			return next;
		});

		setItems((prevItems) =>
			prevItems.map((item) => (item.id === id ? { ...item, approved } : item))
		);

		timesService
			.approve([{ id, approved }])
			.then((response) => {
				if (response && response.ok) {
					setError(null);
				} else {
					let errorMessage = "Error en la respuesta del servidor";
					if (response && response.errors && Array.isArray(response.errors)) {
						const firstError = response.errors[0];
						if (firstError && firstError.description) {
							errorMessage = firstError.description;
						} else if (firstError && firstError.key) {
							errorMessage = `Error: ${firstError.key}`;
						}
					} else if (response && response.message) {
						errorMessage = response.message;
					}
					throw new Error(errorMessage);
				}
			})
			.catch((error) => {
				console.error(
					`Error al ${approved ? "aprobar" : "desaprobar"} el registro ${id}:`,
					error
				);
				setItems((prevItems) =>
					prevItems.map((item) =>
						item.id === id ? { ...item, approved: !approved } : item
					)
				);
				const errorMessage =
					error.message ||
					`Error al ${
						approved ? "aprobar" : "desaprobar"
					} el registro. Por favor, inténtelo de nuevo.`;
				setError(errorMessage);
			})
			.finally(() => {
				approvalLockRef.current.end(key);
				setApprovingIds((prev) => {
					const next = new Set(prev);
					next.delete(key);
					return next;
				});
			});
	}, []);

	// Reiniciar cuando cambian los filtros
	useEffect(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		alertsCacheRef.current = [];
		fetchedAlertEmployeeIdsRef.current = new Set();
		setPage(0);
		setItems([]);
		setHasMore(true);
	}, [filterParams]);

	// Cargar datos desde la API
	useEffect(() => {
		const loadData = async () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			const newAbortController = new AbortController();
			abortControllerRef.current = newAbortController;

			if (page === 0) {
				setLoading(true);
			} else {
				setLoadingMore(true);
			}

			try {
				const apiParams = {
					employee_ids: filterParams?.employeeIds || "",
					start_from: filterParams?.startDate || "",
					start_to: filterParams?.endDate || "",
					...(filterParams?.groupId
						? { group_id: filterParams.groupId }
						: {}),
					page: page,
					page_size: itemsPerPage,
					sort: getSortParam(),
					not_ended: filterParams?.notEnded === "1",
					include_supervised: shouldIncludeSupervised(),
					pending_only: filterParams?.approved === "1",
				};

				const response = await timesService.getAll(
					apiParams,
					newAbortController.signal
				);

				if (response && response.ok && Array.isArray(response.data)) {
					// 1) Mostrar items INMEDIATAMENTE con las alertas que ya tengamos en cache
					const itemsWithCachedAlerts =
						alertsCacheRef.current.length > 0
							? enrichTimeListWithAlerts(
									response.data,
									alertsCacheRef.current
							  )
							: response.data.map((item) => ({
									...item,
									alert: null,
									alerts: [],
							  }));

					if (page === 0) {
						setItems(itemsWithCachedAlerts);
					} else {
						setItems((prev) => [...prev, ...itemsWithCachedAlerts]);
					}

					// 2) Fetch alertas de empleados nuevos en BACKGROUND (no bloquea)
					const responseEmployeeIds = getEmployeeIdsFromItems(response.data);
					const newAlertEmployeeIds = responseEmployeeIds.filter(
						(id) => !fetchedAlertEmployeeIdsRef.current.has(id)
					);

					if (newAlertEmployeeIds.length > 0) {
						newAlertEmployeeIds.forEach((id) =>
							fetchedAlertEmployeeIdsRef.current.add(id)
						);

						// Notificar a planned-hours con los mismos IDs
						if (onNewEmployeeIdsRef.current) {
							onNewEmployeeIdsRef.current(newAlertEmployeeIds);
						}

						getTimeAlerts(newAbortController.signal, newAlertEmployeeIds)
							.then((newAlerts) => {
								if (newAlerts.length === 0) return;
								alertsCacheRef.current = [
									...alertsCacheRef.current,
									...newAlerts,
								];
								// Re-enriquecer todos los items con las alertas actualizadas
								setItems((prev) =>
									enrichTimeListWithAlerts(prev, alertsCacheRef.current)
								);
							})
							.catch((err) => {
								if (err.name !== "AbortError") {
									console.error("Error fetching alerts:", err);
								}
							});
					}

					// Si la página devuelve menos items que el tamaño solicitado,
					// sabemos que ya no hay más. Esto evita loops de InfiniteScroll
					// disparando fetches extra cuando ya se llegó al final.
					setHasMore(response.data.length >= itemsPerPage);
					setError(null);

					if (response.aggregates) {
						setAggregates({
							overall_seconds: response.aggregates.overall_seconds || 0,
							working_seconds: response.aggregates.working_seconds || 0,
						});
					}
			} else {
				if (page === 0) {
					setItems([]);
				}
				setHasMore(false);
				setError("Error al cargar los registros de asistencia");
			}
		} catch (err) {
			if (err.name === "AbortError") return;
			if (page === 0) {
				setItems([]);
			}
			setHasMore(false);
			setError("Error al cargar los registros de asistencia");
			console.error("Error en loadData:", err);
			} finally {
				setLoading(false);
				setLoadingMore(false);
			}
		};

		loadData();

		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, [filterParams, page, getSortParam, getTimeAlerts, enrichTimeListWithAlerts]);

	const filteredItems = useMemo(() => {
		if (!items || items.length === 0) return [];

		if (parseInt(filterParams?.alerts) === 0) {
			return items.filter((t) => !t.alerts || t.alerts.length === 0);
		}
		if (parseInt(filterParams?.alerts) === 1) {
			return items.filter((t) => t.alerts && t.alerts.length > 0);
		}
		return items;
	}, [items, filterParams?.alerts]);

	const stats = useMemo(() => {
		const totalTimeFormatted = formatSecondsToTime(aggregates.overall_seconds);
		const totalWorking = formatSecondsToTime(aggregates.working_seconds);

		return {
			total: totalTimeFormatted,
			totalWorking,
		};
	}, [aggregates]);

	return {
		items: filteredItems,
		loading,
		loadingMore,
		error,
		hasMore,
		fetchMoreData,
		handleSort,
		applySort,
		sortConfig,
		handleApprove,
		approvingIds,
		stats,
		allItems: items,
		setItems,
	};
}
